import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError, SessionExpiredError, type ReportReason } from '../lib/api-client';

type ReportMessage = { key: string } | { text: string };

function reportError(error: unknown): ReportMessage {
  if (error instanceof SessionExpiredError) return { key: 'report.errors.unauthorized' };
  if (error instanceof ApiError) {
    switch (error.status) {
      case 0: return { key: 'report.errors.network' };
      case 400: return { key: 'report.errors.invalidTarget' };
      case 401: return { key: 'report.errors.unauthorized' };
      case 403: return { key: 'report.errors.forbidden' };
      case 404: return { key: 'report.errors.notFound' };
      case 422: return { key: 'report.errors.validation' };
      case 429: return { key: 'report.errors.rateLimit' };
    }
  }
  return { key: 'report.errors.failed' };
}

export default function ReportButton({ subjectType, streamerId, streamKey, enabled = true }: {
  subjectType: 'live_room' | 'profile'; streamerId: string; streamKey?: string | null; enabled?: boolean;
}) {
  const { t } = useTranslation('watch');
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason>('suspected_minor');
  const [details, setDetails] = useState('');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState<ReportMessage | null>(null);
  if (!enabled) return null;
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setSending(true); setMessage(null);
    try {
      await apiClient.createContentReport({ subject_type: subjectType, streamer_id: streamerId, stream_key: streamKey, reason, details });
      setMessage({ key: 'report.success' }); setDetails('');
    } catch (error) { setMessage(reportError(error)); }
    finally { setSending(false); }
  };

  return <>
    <button onClick={() => setOpen(true)} className="rounded border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50">⚑ {t('report.button')}</button>
    {open && <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="report-title">
      <form onSubmit={submit} className="w-full max-w-lg rounded-lg bg-white p-6 text-left shadow-xl">
        <div className="mb-5 flex items-center justify-between"><h2 id="report-title" className="text-xl font-bold">{t('report.title')}</h2><button type="button" onClick={() => setOpen(false)} aria-label={t('report.close')} className="text-2xl">×</button></div>
        <label className="block font-semibold">{t('report.reason')}
          <select value={reason} onChange={e => setReason(e.target.value as ReportReason)} className="mt-2 w-full rounded border border-gray-300 p-3">
            <option value="suspected_minor">{t('report.reasons.suspected_minor')}</option><option value="prohibited_content">{t('report.reasons.prohibited_content')}</option><option value="rights_violation">{t('report.reasons.rights_violation')}</option><option value="other">{t('report.reasons.other')}</option>
          </select>
        </label>
        <label className="mt-5 block font-semibold">{t('report.details')}
          <textarea value={details} onChange={e => setDetails(e.target.value)} maxLength={2000} rows={5} className="mt-2 w-full rounded border border-gray-300 p-3" placeholder={t('report.placeholder')} />
        </label>
        {message && <p className="mt-4 text-sm font-semibold" role="status">{'key' in message ? t(message.key) : message.text}</p>}
        <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={() => setOpen(false)} className="rounded bg-gray-200 px-5 py-2">{t('report.cancel')}</button><button disabled={sending} className="rounded bg-red-700 px-5 py-2 font-bold text-white disabled:opacity-50">{sending ? t('report.sending') : t('report.send')}</button></div>
      </form>
    </div>}
  </>;
}
